import type {
  AdminNotificationDelivery,
  NotificationEventTypeName,
} from '@steado/admin-contract/notifications';

import type { ToneName } from '@/components/table/tone';

/** What each registry event is, in the words the app's user would use. */
export const EVENT_TYPE_LABELS: Readonly<Record<NotificationEventTypeName, string>> = {
  'plan.ready': 'Plan ready',
  'plan.failed': 'Plan failed',
  'meal_plan.ready': 'Meal plan ready',
  'meal_plan.failed': 'Meal plan failed',
  'food_scan.ready': 'Meal scan ready',
  'food_scan.failed': 'Meal scan failed',
  'billing.issue': 'Billing issue',
  'billing.access_will_end': 'Access ending soon',
  'billing.expired': 'Subscription expired',
  'security.session_reuse': 'Session reuse alert',
  'security.new_device_login': 'New device sign-in',
  'workout.reminder': 'Workout reminder',
  'meal.reminder': 'Meal reminder',
  'coaching.tip': 'Coaching tip',
  'weekly.summary': 'Weekly summary',
  'monthly.recap': 'Monthly recap',
  'streak.at_risk': 'Streak at risk',
  'rhythm.day_open': 'Day still open',
  'rhythm.winback': 'Win-back',
  'admin.announcement': 'Broadcast',
  'gym.member_approved': 'Gym approved',
  'gym.member_declined': 'Gym declined',
  'gym.plan_published': 'Trainer plan published',
  'gym.trainer_changed': 'Trainer changed',
  'gym.coaching_ended': 'Gym coaching ended',
  'gym.trainer_message': 'Gym trainer message',
  'gym.request_answered': 'Gym request answered',
  'gym.workout_logged': 'Workout logged by trainer',
  'activity.synced': 'Workouts synced from a health store',
};

/** An event type the console does not know yet still reads, as its raw name. */
export function eventTypeLabel(eventType: string): string {
  const labels: Readonly<Record<string, string | undefined>> = EVENT_TYPE_LABELS;
  return labels[eventType] ?? eventType;
}

export const PLATFORM_LABELS: Readonly<
  Record<AdminNotificationDelivery['installation']['platform'], string>
> = {
  ANDROID: 'Android',
  IOS: 'iOS',
  WEB: 'Web',
};

type DeliveryStatus = AdminNotificationDelivery['status'];

export const DELIVERY_STATUS_LABELS: Readonly<Record<DeliveryStatus, string>> = {
  SENT: 'Sent',
  SCHEDULED: 'Scheduled',
  PROCESSING: 'Sending',
  DEFERRED: 'Deferred',
  SUPPRESSED: 'Held back',
  FAILED_RETRYABLE: 'Failed, will retry',
  FAILED_TERMINAL: 'Failed',
};

/**
 * How each delivery outcome should read.
 *
 * Held back is muted rather than red: it is the policy engine working as
 * designed, and a screenful of red for quiet hours teaches an operator to
 * ignore the colour everywhere else.
 */
export const DELIVERY_STATUS_TONES: Readonly<Record<DeliveryStatus, ToneName>> = {
  SENT: 'ok',
  SCHEDULED: 'info',
  PROCESSING: 'info',
  DEFERRED: 'purple',
  SUPPRESSED: 'mute',
  FAILED_RETRYABLE: 'warn',
  FAILED_TERMINAL: 'err',
};
